import React, { useState, useEffect } from 'react';
import { X, Star, Download, Shield, Clock } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if user has EVER dismissed the prompt - respect their choice permanently
    const dismissed = localStorage.getItem('pwaPromptDismissed');
    if (dismissed) {
      // User has dismissed before - don't show again
      return;
    }
    
    // Also check session storage to prevent showing multiple times per session
    const sessionDismissed = sessionStorage.getItem('pwaPromptDismissedSession');
    if (sessionDismissed) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after 60 seconds (give user time to use the app first)
      setTimeout(() => setShowPrompt(true), 60000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show after delay (60 seconds)
    if (isIOSDevice) {
      setTimeout(() => setShowPrompt(true), 60000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSSteps(true);
      return;
    }
    
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSSteps(false);
    // Permanently remember the user's choice - don't nag them again
    localStorage.setItem('pwaPromptDismissed', 'true');
    // Also set session storage as backup
    sessionStorage.setItem('pwaPromptDismissedSession', 'true');
  };

  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={handleDismiss}
      />
      
      {/* App Store Style Modal */}
      <div 
        className="fixed inset-x-4 bottom-4 md:inset-auto md:bottom-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[380px] z-50"
        data-testid="pwa-install-prompt"
      >
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
          
          {/* Close Button */}
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {!showIOSSteps ? (
            <>
              {/* App Header - Like App Store */}
              <div className="p-6 pb-4">
                <div className="flex gap-4">
                  {/* App Icon */}
                  <div className="w-20 h-20 rounded-[22px] bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] shadow-lg flex items-center justify-center flex-shrink-0">
                    <img 
                      src="/coregulate-mark-128x128.png" 
                      alt="CoRegulate"
                      className="w-14 h-14"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  
                  {/* App Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#1a1a1a] text-lg leading-tight">
                      CoRegulate
                    </h3>
                    <p className="text-[#8e8e93] text-sm mt-0.5">
                      ADHD Emotional Regulation
                    </p>
                    
                    {/* Rating */}
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="flex">
                        {[1,2,3,4,5].map((star) => (
                          <Star 
                            key={star} 
                            className={`w-3.5 h-3.5 ${star <= 5 ? 'text-[#FF9500] fill-[#FF9500]' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-[#8e8e93]">4.9 • 2.4K Ratings</span>
                    </div>
                  </div>
                </div>

                {/* Install Button */}
                <button
                  onClick={handleInstall}
                  className="w-full mt-5 bg-[#007AFF] hover:bg-[#0056CC] text-white font-semibold py-3.5 rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  {isIOS ? 'Install App' : 'GET'}
                </button>

                {/* In-app purchases note like App Store */}
                <p className="text-center text-[#8e8e93] text-xs mt-2">
                  Free • Optional in-app purchases
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-100 mx-6" />

              {/* App Preview Section */}
              <div className="p-6 pt-4">
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
                  {/* Preview Cards */}
                  <div className="flex-shrink-0 w-28 h-48 rounded-2xl bg-gradient-to-br from-[#FBE0C3] to-[#FEF6EF] flex items-center justify-center">
                    <div className="text-center px-2">
                      <div className="w-10 h-10 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-2">
                        <Clock className="w-5 h-5 text-[#1D6B6B]" />
                      </div>
                      <p className="text-xs text-[#344648] font-medium">Quick<br/>Exercises</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-28 h-48 rounded-2xl bg-gradient-to-br from-[#E8F5F5] to-[#F0FAFA] flex items-center justify-center">
                    <div className="text-center px-2">
                      <div className="w-10 h-10 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-2">
                        <Shield className="w-5 h-5 text-[#1D6B6B]" />
                      </div>
                      <p className="text-xs text-[#344648] font-medium">Works<br/>Offline</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-28 h-48 rounded-2xl bg-gradient-to-br from-[#FFF5EC] to-[#FFFAF5] flex items-center justify-center">
                    <div className="text-center px-2">
                      <div className="w-10 h-10 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-2">
                        <svg className="w-5 h-5 text-[#E88B5A]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                      </div>
                      <p className="text-xs text-[#344648] font-medium">ADHD<br/>Friendly</p>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-[#3a3a3c]">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Instant access from home screen</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#3a3a3c]">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>Use calming tools even offline</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#3a3a3c]">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>No app store required</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* iOS Installation Steps */
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1D6B6B] to-[#FFBB98] shadow-lg flex items-center justify-center mx-auto mb-4">
                  <img 
                    src="/coregulate-mark-128x128.png" 
                    alt="CoRegulate"
                    className="w-10 h-10"
                  />
                </div>
                <h3 className="font-semibold text-[#1a1a1a] text-lg">
                  Install CoRegulate
                </h3>
                <p className="text-[#8e8e93] text-sm mt-1">
                  Add to your home screen in 3 easy steps
                </p>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                <div className="flex gap-4 items-start p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">Tap this button at the bottom of Safari</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                      <svg className="w-6 h-6 text-[#007AFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3V15" />
                      </svg>
                      <span className="text-sm text-[#8e8e93]">(square with arrow)</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 items-start p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">Scroll down in the menu & tap</p>
                    <div className="mt-2 inline-flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                      <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <span className="text-sm text-[#1a1a1a] font-medium">Add to Home Screen</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 items-start p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-[#007AFF] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">Tap "Add" in the top right</p>
                    <p className="text-[#8e8e93] text-xs mt-0.5">
                      That's it! CoRegulate will appear on your home screen
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleDismiss}
                className="w-full mt-6 bg-gray-100 hover:bg-gray-200 text-[#1a1a1a] font-medium py-3 rounded-xl transition-all"
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
